// FilterModal.jsx — exact copy of ServiceProviderFilter behavior (slide-in panel / centered on mobile)
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaMapMarkerAlt, FaMoneyBillWave, FaStar, FaConciergeBell, FaRedo, FaCheck, FaWifi, FaSwimmingPool, FaSpa, FaDumbbell, FaUtensils, FaGlassCheers, FaParking, FaSnowflake, FaTv, FaHotel, FaBuilding, FaMapPin } from 'react-icons/fa';
import './css/FilterModal.css';

const AMENITIES = [
    { name: 'WiFi', icon: <FaWifi /> },
    { name: 'Swimming Pool', icon: <FaSwimmingPool /> },
    { name: 'Spa', icon: <FaSpa /> },
    { name: 'Gym', icon: <FaDumbbell /> },
    { name: 'Restaurant', icon: <FaUtensils /> },
    { name: 'Bar', icon: <FaGlassCheers /> },
    { name: 'Parking', icon: <FaParking /> },
    { name: 'Air Conditioning', icon: <FaSnowflake /> },
    { name: 'TV', icon: <FaTv /> },
    { name: 'Room Service', icon: <FaConciergeBell /> }
];

const PRICE_PRESETS = [
    { label: '< 25k', min: '', max: '25000' },
    { label: '25k – 50k', min: '25000', max: '50000' },
    { label: '50k – 100k', min: '50000', max: '100000' },
    { label: '100k – 200k', min: '100000', max: '200000' },
    { label: '> 200k', min: '200000', max: '' }
];

const EMPTY_FILTERS = {
    name: '',
    city: '',
    location: '',
    minPrice: '',
    maxPrice: '',
    rating: '',
    amenities: []
};

const FilterModal = ({ isOpen, onClose, onApplyFilters, filters: initialFilters, mode = 'hotel' }) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const isPropertyMode = mode === 'property';

    useEffect(() => {
        if (!isOpen) return;
        setFilters(initialFilters);
        requestAnimationFrame(() => setIsVisible(true));
    }, [isOpen, initialFilters]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen]);

    // Lock body scroll
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 250);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handlePricePreset = (preset) => {
        setFilters(prev => {
            const isActive = prev.minPrice === preset.min && prev.maxPrice === preset.max;
            return { ...prev, minPrice: isActive ? '' : preset.min, maxPrice: isActive ? '' : preset.max };
        });
    };

    const handleAmenityToggle = (amenity) => {
        setFilters(prev => ({
            ...prev,
            amenities: prev.amenities.includes(amenity)
                ? prev.amenities.filter(a => a !== amenity)
                : [...prev.amenities, amenity]
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onApplyFilters) onApplyFilters(filters);
        handleClose();
    };

    const handleReset = () => {
        setFilters({ ...EMPTY_FILTERS });
        if (onApplyFilters) onApplyFilters({ ...EMPTY_FILTERS });
    };

    const activeCount = Object.entries(filters).reduce((count, [key, value]) => {
        if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
        return count + (value !== '' && value != null ? 1 : 0);
    }, 0);

    const filterUI = (
        <div
            className={`sf-filter-overlay ${isVisible ? 'sf-filter-overlay--visible' : ''}`}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div className={`sf-filter-panel ${isVisible ? 'sf-filter-panel--open' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="sf-filter-header">
                    <div className="sf-filter-header-left">
                        <h2>{t('filter_modal.title')}</h2>
                        {activeCount > 0 && <span className="sf-filter-badge">{activeCount}</span>}
                    </div>
                    <button className="sf-filter-close" onClick={handleClose} aria-label="Close filters">
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="sf-filter-body">
                    {/* Location */}
                    <div className="sf-filter-section">
                        <h3><FaMapMarkerAlt /> {t('filter_modal.location')}</h3>
                        <div className="sf-filter-grid">
                            <div className="sf-filter-field">
                                <label>{isPropertyMode ? t('filter_modal.property_name') : t('filter_modal.hotel_name')}</label>
                                <div className="sf-filter-input-wrap">
                                    <FaHotel className="sf-filter-input-icon" />
                                    <input type="text" placeholder={t('filter_modal.name_placeholder')} value={filters.name} onChange={(e) => handleFilterChange('name', e.target.value)} />
                                </div>
                            </div>
                            <div className="sf-filter-field">
                                <label>{t('filter_modal.city')}</label>
                                <div className="sf-filter-input-wrap">
                                    <FaBuilding className="sf-filter-input-icon" />
                                    <input type="text" placeholder={t('filter_modal.city_placeholder')} value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)} />
                                </div>
                            </div>
                            <div className="sf-filter-field">
                                <label>{t('filter_modal.area')}</label>
                                <div className="sf-filter-input-wrap">
                                    <FaMapPin className="sf-filter-input-icon" />
                                    <input type="text" placeholder={t('filter_modal.area_placeholder')} value={filters.location} onChange={(e) => handleFilterChange('location', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Price */}
                    <div className="sf-filter-section">
                        <h3><FaMoneyBillWave /> {t('filter_modal.price')}</h3>
                        <div className="sf-filter-price-row">
                            <div className="sf-filter-input-wrap">
                                <input type="number" min="0" placeholder={t('filter_modal.min_price')} value={filters.minPrice} onChange={(e) => handleFilterChange('minPrice', e.target.value)} />
                            </div>
                            <span className="sf-filter-price-sep">—</span>
                            <div className="sf-filter-input-wrap">
                                <input type="number" min="0" placeholder={t('filter_modal.max_price')} value={filters.maxPrice} onChange={(e) => handleFilterChange('maxPrice', e.target.value)} />
                            </div>
                        </div>
                        <div className="sf-filter-chips">
                            {PRICE_PRESETS.map(preset => (
                                <button
                                    key={preset.label}
                                    type="button"
                                    className={`sf-filter-chip ${filters.minPrice === preset.min && filters.maxPrice === preset.max ? 'sf-filter-chip--active' : ''}`}
                                    onClick={() => handlePricePreset(preset)}
                                >
                                    {filters.minPrice === preset.min && filters.maxPrice === preset.max && <FaCheck />} {preset.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Rating — hidden in property mode */}
                    {!isPropertyMode && (
                        <div className="sf-filter-section">
                            <h3><FaStar /> {t('filter_modal.rating')}</h3>
                            <div className="sf-filter-chips">
                                {[1, 2, 3, 4, 5].map(rating => (
                                    <button
                                        key={rating}
                                        type="button"
                                        className={`sf-filter-chip ${filters.rating === rating.toString() ? 'sf-filter-chip--active' : ''}`}
                                        onClick={() => handleFilterChange('rating', filters.rating === rating.toString() ? '' : rating.toString())}
                                    >
                                        {filters.rating === rating.toString() && <FaCheck />} {rating}+ <FaStar />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Amenities — hidden in property mode */}
                    {!isPropertyMode && (
                        <div className="sf-filter-section sf-filter-section--last">
                            <h3><FaConciergeBell /> {t('filter_modal.amenities')}</h3>
                            <div className="sf-filter-chips">
                                {AMENITIES.map(({ name, icon }) => {
                                    const active = filters.amenities.includes(name);
                                    return (
                                        <button
                                            key={name}
                                            type="button"
                                            className={`sf-filter-chip ${active ? 'sf-filter-chip--active' : ''}`}
                                            onClick={() => handleAmenityToggle(name)}
                                        >
                                            {active && <FaCheck />} {icon} {name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="sf-filter-actions">
                        <button type="submit" className="sf-filter-apply">
                            {t('filter_modal.apply')} {activeCount > 0 && `(${activeCount})`}
                        </button>
                        <button type="button" className="sf-filter-reset" onClick={handleReset}>
                            <FaRedo /> {t('filter_modal.reset')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return createPortal(filterUI, document.body);
};

export default FilterModal;
